import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonChecked,
  Schedule,
  LinkedIn,
  Facebook,
  Instagram,
} from '@mui/icons-material';

const platformIcons = {
  linkedin: LinkedIn,
  facebook: Facebook,
  instagram: Instagram,
};

export default function HorizontalProgressStepper({ runId, progressData }) {
  const steps = [
    { id: 1, name: 'Job Data Processing' },
    { id: 2, name: 'AI Image Generation' },
    { id: 3, name: 'Caption Generation' },
    { id: 4, name: 'Design Creation' },
    { id: 5, name: 'Export & Finalize' },
  ];

  const isStepCompleted = (stepIndex) => {
    return progressData && progressData.current_step > stepIndex + 1;
  };

  const isStepActive = (stepIndex) => {
    return progressData && progressData.current_step === stepIndex + 1;
  };

  if (!progressData) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Loading automation progress...
        </Typography>
      </Paper>
    );
  }

  const { data = {}, status } = progressData;
  const { platforms = [], current_platform, designs_completed = 0, total_designs = 0, captions = {} } = data;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          🚀 Automation Pipeline
        </Typography>
        <Chip
          label={status === 'in_progress' ? 'IN PROGRESS' : status === 'completed' ? 'COMPLETED' : 'PENDING'}
          color={status === 'in_progress' ? 'primary' : status === 'completed' ? 'success' : 'default'}
          size="small"
        />
      </Box>

      {/* Platform and Design Info */}
      {platforms.length > 0 && (
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Platforms
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              {platforms.map((platform) => {
                const PlatformIcon = platformIcons[platform] || Schedule;
                return (
                  <Chip
                    key={platform}
                    icon={<PlatformIcon />}
                    label={platform}
                    size="small"
                    variant={platform === current_platform ? 'filled' : 'outlined'}
                    color={platform === current_platform ? 'primary' : 'default'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                );
              })}
            </Stack>
          </Box>

          {total_designs > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Progress
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                {designs_completed} / {total_designs} designs
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Progress Steps */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        {steps.map((step, index) => {
          const active = isStepActive(index);
          const completed = isStepCompleted(index);

          return (
            <React.Fragment key={step.id}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.200',
                    color: completed || active ? 'white' : 'text.secondary',
                    mb: 1,
                  }}
                >
                  {completed ? <CheckCircle /> : active ? <RadioButtonChecked /> : <Schedule />}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    fontWeight: active ? 600 : 400,
                    color: active ? 'primary.main' : 'text.secondary',
                    fontSize: '0.7rem',
                  }}
                >
                  {step.name}
                </Typography>
              </Box>

              {index < steps.length - 1 && (
                <Box sx={{ width: 40, height: 2, bgcolor: completed ? 'success.main' : 'grey.200', mt: -3 }} />
              )}
            </React.Fragment>
          );
        })}
      </Stack>

      {/* Generated Captions Preview */}
      {Object.keys(captions).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Generated Captions
          </Typography>
          {Object.entries(captions).map(([platform, caption]) => {
            if (!caption) return null;
            const PlatformIcon = platformIcons[platform] || Schedule;
            return (
              <Box key={platform} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <PlatformIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                    {platform}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                  {caption.length > 150 ? caption.substring(0, 150) + '...' : caption}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Overall Progress Bar */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Overall Progress
          </Typography>
          <Typography variant="caption" fontWeight={600} color="primary">
            Step {progressData.current_step || 0} of {progressData.total_steps || 5}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={((progressData.current_step || 0) / (progressData.total_steps || 5)) * 100}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>
    </Paper>
  );
}
